import type { ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { Folder, LayersThree01, Send01 } from '@kula-ai/icons-core';
import type { AnonymizeCandidate } from './AnonymizeCandidateModal';

interface RecordGroupProps {
  icon: ReactNode;
  label: string;
  items: string[];
  emptyText: string;
}

// One "Currently part of" row: a labelled icon, then the record names or an
// empty note. Names come pre-extracted so the group stays presentational.
const RecordGroup = ({ icon, label, items, emptyText }: RecordGroupProps) => (
  <Box>
    <Flex align="center" gap="8px">
      <Box as="span" display="inline-flex" color="secondary-700">
        {icon}
      </Box>
      <Text variant="p" color="heading">
        {label}
      </Text>
    </Flex>
    <Box pl="26px" mt="2px">
      {items.length ? (
        items.map((name, index) => (
          <Text key={`${name}-${index}`} variant="p" color="body">
            {name}
          </Text>
        ))
      ) : (
        <Text variant="p" color="muted">
          {emptyText}
        </Text>
      )}
    </Box>
  </Box>
);

const names = (list: unknown, key: string): string[] =>
  (Array.isArray(list) ? list : [])
    .map((item) => (item as Record<string, unknown>)?.[key])
    .filter((v): v is string => typeof v === 'string' && v.length > 0);

// The jobs / projects / flows the candidate is currently on — shown so the
// recruiter sees what stays linked (for reporting) after anonymizing.
export const AnonymizeRecordsList = ({
  candidate,
}: {
  candidate: AnonymizeCandidate;
}) => (
  <Flex flexDir="column" gap="16px">
    <RecordGroup
      icon={<LayersThree01 size={16} />}
      label="Jobs"
      items={names(candidate.ats_applications, 'ats_job_name')}
      emptyText="No jobs"
    />
    <RecordGroup
      icon={<Folder size={16} />}
      label="Projects"
      items={names(candidate.projects, 'name')}
      emptyText="No projects"
    />
    <RecordGroup
      icon={<Send01 size={16} />}
      label="Flows"
      items={names(candidate.outreaches, 'title')}
      emptyText="No flows"
    />
  </Flex>
);
